import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import useMobile from "../../hooks/useMobile";
import { Link } from "react-router-dom";
import ConfirmPopUp from "../ConfirmPopUp";
import DeletePopUp from "../DeletePopUp";
import { ICooperative } from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { fetchCooperative } from "../../api/DashboardApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { darkenColor } from "../../exports/functions";
import { usePermission } from "../../hooks/usePermission";
import { useRole } from "../../hooks/useRole";
import TableFooter from "../TableFooter";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const CooperativeTable = ({
  cooperative,
  setCooperative,
}: {
  cooperative: ICooperative[];
  setCooperative: Dispatch<SetStateAction<ICooperative[]>>;
}) => {
  const { categories, statusOptions } = useOption();
  const { isSuperAdmin, isAdmin } = useRole();
  const { user } = useUser();
  const { sendNotification } = useNotification();

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Cooperative Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const natureStyle = (nature: string) => {
    const natureOptions =
      categories?.find(
        (item) => item.name === "Nature of Work / Notice Audience",
      )?.dropdowns || [];

    const matchedNature = natureOptions.find(
      (item) => item.label?.toLowerCase() === nature?.toLowerCase(),
    );

    return {
      backgroundColor: matchedNature?.color || "#ccc",
      color: darkenColor(matchedNature?.color || "#ccc", 40),
    };
  };

  const natureOfWorkOptions =
    categories
      ?.find((item) => item.name === "Nature of Work / Notice Audience")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const { setLoading } = useLoader();
  const { canEdit, canDelete } = usePermission();
  const isMobile = useMobile();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [openStatusId, setOpenStatusId] = useState<number | null>(null);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [selectedStatusValue, setSelectedStatusValue] = useState<string>("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [statusDropPos, setStatusDropPos] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [natureFilter, setNatureFilter] = useState<string | null>(null);
  const [openHeaderDropdown, setOpenHeaderDropdown] = useState<
    "status" | "nature" | null
  >(null);
  const [headerDropdownPos, setHeaderDropdownPos] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });
  const statusThRef = useRef<HTMLTableCellElement>(null);
  const natureThRef = useRef<HTMLTableCellElement>(null);
  const itemsPerPage = 25;

  const handleStatusBadgeClick = (
    id: number,
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = Math.min(cooperativeStatuses.length * 42 + 12, 260);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const direction = (() => {
      if (spaceBelow >= menuHeight) return "down";
      if (spaceAbove >= menuHeight) return "up";
      return spaceAbove > spaceBelow ? "up" : "down";
    })();

    const top =
      direction === "down"
        ? rect.bottom + window.scrollY
        : rect.top + window.scrollY - menuHeight;

    setStatusDropPos({ top, left: rect.left + window.scrollX });
    setOpenStatusId((prev) => (prev === id ? null : id));
  };

  const filteredData = cooperative.filter((item) => {
    if (statusFilter && item.status !== statusFilter) return false;
    if (natureFilter && item.natureOfWork !== natureFilter) return false;
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = showAll
    ? filteredData
    : filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const cooperativeStatuses =
    statusOptions?.find((item) => item.name === "Cooperative Statuses")
      ?.statusOptions || [];

  const handleClose = () => {
    setShowDelete(false);
  };
  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/registers/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchCooperative()
          .then((cooperativeData) => {
            setCooperative(cooperativeData);
            setShowDelete(false);
            setDeleteId(null);
            sendNotification(
              `Cooperative deleted`,
              user?.user?.userName || "User",
            );
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Cooperative deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
            setTimeout(() => {
              setLoading(false);
            }, 1000);
          });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleStatusSelect = (id: number, status: string) => {
    setSelectedStatusId(id);
    setSelectedStatusValue(status);
    setShowConfirm(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedStatusId === null) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("status", selectedStatusValue);

      const response = await axiosClient.put(
        `/registers/${selectedStatusId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.status === 200 || response.status === 201) {
        setCooperative((prev) =>
          prev.map((item) =>
            item.id === selectedStatusId
              ? {
                  ...item,
                  status: selectedStatusValue,
                }
              : item,
          ),
        );
        sendNotification(
          `Cooperative status updated`,
          user?.user?.userName || "User",
        );
        dynamicSuccess("Status updated successfully!");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to update status.";
      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setShowConfirm(false);
        setOpenStatusId(null);
        setSelectedStatusId(null);
        setSelectedStatusValue("");
      }, 1000);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenStatusId(null);
      setOpenHeaderDropdown(null);
    };
    if (openStatusId !== null || openHeaderDropdown !== null) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openStatusId, openHeaderDropdown]);

  return (
    <>
      <div id="cooperative-table" className="table-dashboard-card">
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th className="table-xs">S. no.</th>
                <th className="table-sm">Reg no.</th>
                <th className="table-sm">Business no.</th>
                <th className="table-md">Name</th>
                <th className="table-sm">Reg. PAN no.</th>
                <th className="table-md">Email Address</th>
                <th className="table-sm">Mobile no.</th>
                <th
                  ref={statusThRef}
                  className="table-md cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (openHeaderDropdown === "status") {
                      setOpenHeaderDropdown(null);
                    } else {
                      const rect = statusThRef.current?.getBoundingClientRect();
                      if (rect) {
                        setHeaderDropdownPos({
                          top: rect.bottom + window.scrollY,
                          left: rect.left + window.scrollX,
                        });
                      }
                      setOpenHeaderDropdown("status");
                    }
                  }}
                >
                  Status{" "}
                  <i
                    className="fa-solid fa-chevron-down ms-1"
                    style={{ color: "#d63d2c" }}
                  ></i>
                </th>
                <th
                  ref={natureThRef}
                  className="table-md cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (openHeaderDropdown === "nature") {
                      setOpenHeaderDropdown(null);
                    } else {
                      const rect = natureThRef.current?.getBoundingClientRect();
                      if (rect) {
                        setHeaderDropdownPos({
                          top: rect.bottom + window.scrollY,
                          left: rect.left + window.scrollX,
                        });
                      }
                      setOpenHeaderDropdown("nature");
                    }
                  }}
                >
                  Nature of Work{" "}
                  <i
                    className="fa-solid fa-chevron-down ms-1"
                    style={{ color: "#d63d2c" }}
                  ></i>
                </th>
                {/* <th className="table-sm sticky-action-header">Actions</th> */}
                <th className="table-sm">Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentData.length > 0 ? (
                currentData.map((item, i) => (
                  <tr key={i}>
                    <td
                      className="table-xs text-danger"
                      title={String(indexOfFirstItem + i + 1)}
                    >
                      {String(indexOfFirstItem + i + 1).padStart(2, "0")}
                    </td>

                    <td
                      className="table-sm text-danger"
                      title={item.registrationNumber || "-"}
                    >
                      {item.registrationNumber || "-"}
                    </td>
                    <td
                      className="table-sm text-danger"
                      title={item.businessregistrationNumber || "-"}
                    >
                      {item.businessregistrationNumber || "-"}
                    </td>
                    <td
                      className="table-md table-bold"
                      title={item.nameEnglish || "-"}
                    >
                      {item.nameEnglish || "-"}
                    </td>
                    <td className="table-sm" title={item.panNumber || "-"}>
                      {item.panNumber || "-"}
                    </td>
                    <td className="table-md" title={item.email || "-"}>
                      {item.email || "-"}
                    </td>
                    <td className="table-sm" title={item.mobileNumber || "-"}>
                      {item.mobileNumber || "-"}
                    </td>

                    <td className="table-md">
                      {isSuperAdmin || isAdmin ? (
                        <div
                          className="status-dropdown-container"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span
                            className="badge rounded-pill px-3 py-2 status-badge-clickable mw-100"
                            style={statusStyle(item.status)}
                            onClick={(e) => handleStatusBadgeClick(item.id, e)}
                            title={item.status}
                          >
                            {item.status}
                          </span>
                        </div>
                      ) : (
                        <span
                          className="badge rounded-pill px-3 py-2 mw-100"
                          style={statusStyle(item.status)}
                          title={item.status}
                        >
                          {item.status}
                        </span>
                      )}
                    </td>

                    <td className="table-md">
                      <span
                        className="badge rounded-pill px-3 py-2 mw-100"
                        style={natureStyle(item.natureOfWork)}
                        title={item.natureOfWork || "-"}
                      >
                        {item.natureOfWork || "-"}
                      </span>
                    </td>

                    <td className="table-sm">
                      {/* <td className="table-sm sticky-action"> */}
                      <div className="d-flex gap-2 align-items-center">
                        <Link to={`/cooperatives-list/${item.id}`}>
                          <i
                            className="cursor-pointer"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="View"
                          >
                            <Eye />
                          </i>
                        </Link>
                        <Link
                          to={
                            canEdit("cooperatives")
                              ? `/register/edit/${item.id}`
                              : "#"
                          }
                          onClick={(e) => {
                            if (!canEdit("cooperatives")) {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            pointerEvents: canEdit("cooperatives")
                              ? "auto"
                              : "none",
                            opacity: canEdit("cooperatives") ? 1 : 0.5,
                          }}
                        >
                          <i
                            data-toggle="tooltip"
                            data-placement="top"
                            title={
                              canEdit("cooperatives") ? "Edit" : "No permission"
                            }
                          >
                            <Pencil size={20} />
                          </i>
                        </Link>
                        <i
                          className={`fa-solid fa-trash text-danger ${
                            canDelete("cooperatives")
                              ? "cursor-pointer"
                              : "opacity-50"
                          }`}
                          data-toggle="tooltip"
                          data-placement="top"
                          title={
                            canDelete("cooperatives")
                              ? "Delete"
                              : "No permission"
                          }
                          style={{
                            pointerEvents: canDelete("cooperatives")
                              ? "auto"
                              : "none",
                          }}
                          onClick={() => {
                            if (!canDelete("cooperatives")) return;

                            setDeleteId(item.id);
                            setShowDelete(true);
                          }}
                        ></i>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center py-4 text-muted">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <TableFooter
          isMobile={isMobile}
          totalItems={filteredData.length}
          label="Cooperatives"
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          showAll={showAll}
          onToggleShowAll={() => {
            setShowAll((prev) => !prev);
            setCurrentPage(1);
          }}
          onPageChange={(page) => setCurrentPage(page)}
          className="table-footer"
        />
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
      {showConfirm && (
        <ConfirmPopUp
          onClose={() => setShowConfirm(false)}
          onConfirm={handleConfirmStatusChange}
        />
      )}
      {openStatusId !== null &&
        createPortal(
          <div
            className={`status-dropdown-menu shadow`}
            style={{
              position: "absolute",
              top: statusDropPos.top,
              left: statusDropPos.left,
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {cooperativeStatuses.map((option) => (
              <button
                key={option.id}
                type="button"
                className="status-dropdown-item"
                onClick={() => {
                  if (openStatusId !== null) {
                    handleStatusSelect(openStatusId, option.label);
                  }
                }}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
      {openHeaderDropdown === "status" &&
        createPortal(
          <div
            className="status-dropdown-menu status-dropdown-down shadow header-filter-menu"
            style={{
              position: "absolute",
              top: headerDropdownPos.top - 35,
              left: headerDropdownPos.left,
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={`status-dropdown-item ${!statusFilter ? "active fw-bold" : ""}`}
              onClick={() => {
                setStatusFilter(null);
                setCurrentPage(1);
                setOpenHeaderDropdown(null);
              }}
            >
              All
            </button>
            {cooperativeStatuses.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`status-dropdown-item ${statusFilter === option.label ? "active fw-bold" : ""}`}
                onClick={() => {
                  setStatusFilter(option.label);
                  setCurrentPage(1);
                  setOpenHeaderDropdown(null);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
      {openHeaderDropdown === "nature" &&
        createPortal(
          <div
            className="status-dropdown-menu status-dropdown-down shadow header-filter-menu"
            style={{
              position: "absolute",
              top: headerDropdownPos.top - 35,
              left: headerDropdownPos.left,
              zIndex: 9999,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={`status-dropdown-item ${!natureFilter ? "active fw-bold" : ""}`}
              onClick={() => {
                setNatureFilter(null);
                setCurrentPage(1);
                setOpenHeaderDropdown(null);
              }}
            >
              All
            </button>
            {natureOfWorkOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                className={`status-dropdown-item ${natureFilter === option.value ? "active fw-bold" : ""}`}
                onClick={() => {
                  setNatureFilter(option.value);
                  setCurrentPage(1);
                  setOpenHeaderDropdown(null);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default CooperativeTable;
